import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { SHARED_ENVIRONMENT } from '@cp/shared/environment/environment.token';
import { Environment } from '@cp/shared/environment/environment.interface';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { SharedHttpService } from '../../shared/shared-http.service';
import {
  FirstShiftBookingHttpModel,
  FirstShiftStatusHttpModel,
  FirstShiftTimeSlotHttpModel,
  FirstShiftWasAttendedHttpModel,
} from './first-shift-http.models';
import { FirstShiftHttpPaths } from './first-shift-http.paths';

@Injectable({
  providedIn: 'root',
})
export class FirstShiftHttpService extends SharedHttpService {
  private _refreshActiveBooking = new BehaviorSubject<void>(undefined);
  private _refreshBookingStatus = new BehaviorSubject<void>(undefined);

  public activeBooking$ = this._refreshActiveBooking.pipe(
    switchMap(() =>
      this._http.get<FirstShiftBookingHttpModel | null>(
        this._environment.apiEndpoint + FirstShiftHttpPaths.activeBooking,
      ),
    ),
  );

  public bookingsStatus$ = this._refreshBookingStatus.pipe(
    switchMap(() =>
      this._http.get<FirstShiftStatusHttpModel>(
        this._environment.apiEndpoint + FirstShiftHttpPaths.bookingStatus,
      ),
    ),
  );

  public attended$ = this._http.get<FirstShiftWasAttendedHttpModel | null>(
    this._environment.apiEndpoint + FirstShiftHttpPaths.attended,
  );

  constructor(
    private _http: HttpClient,
    @Inject(SHARED_ENVIRONMENT)
    private _environment: Environment,
  ) {
    super();
  }

  public getWelcomeCallSlots$(
    startDate: string,
    endDate: string,
  ): Observable<Array<FirstShiftTimeSlotHttpModel>> {
    return this._http.get<Array<FirstShiftTimeSlotHttpModel>>(
      this._environment.apiEndpoint + FirstShiftHttpPaths.welcomeCallSlots,
      {
        responseType: 'json',
        params: { startDate, endDate },
      },
    );
  }

  public amendPhoneNumber$(
    phoneNumber: string,
  ): Observable<HttpResponse<object>> {
    return this._http.put(
      this._environment.apiEndpoint + FirstShiftHttpPaths.amendPhoneNumber,
      { phoneNumber },
      { observe: 'response' },
    );
  }

  public cancelAppointment$(): Observable<HttpResponse<object>> {
    return this._http.put(
      this._environment.apiEndpoint + FirstShiftHttpPaths.cancelAppointment,
      null,
      { observe: 'response' },
    );
  }

  public bookTimeSlot$(timeSlotId: number): Observable<HttpResponse<object>> {
    return this._http.post(
      this._environment.apiEndpoint +
        FirstShiftHttpPaths.bookTimeSlot.replace('{###}', `${timeSlotId}`),
      null,
      {
        observe: 'response',
      },
    );
  }

  public updateTimeSlot$(newId: number): Observable<HttpResponse<object>> {
    return this._http.put(
      this._environment.apiEndpoint +
        FirstShiftHttpPaths.rescheduleTimeSlot.replace('{###}', `${newId}`),
      null,
      {
        observe: 'response',
      },
    );
  }

  public reloadActiveBooking(): void {
    this._refreshActiveBooking.next();
  }

  public reloadBookingStatus(): void {
    this._refreshBookingStatus.next();
  }
}
